import * as D from "@radix-ui/react-dropdown-menu"
import { CaretRightIcon, CheckIcon, DotFilledIcon } from "@radix-ui/react-icons"
import { cn } from "@utils/tw"
import { Fragment, ReactElement, ReactNode } from "react"
import { Icon } from "./Icon"
import { DropdownItem, dropdownItemStyles, dropdownMenuStyles } from "./menu-helpers"
import { popoverStyles } from "./Popover"
import { Checkbox } from "./Checkbox"

type DropdownMenuProps = {
  /** Button to trigger the menu to open */
  trigger: ReactElement
  align?: "end" | "start" | "center"
  side?: "top" | "left" | "right" | "bottom"

  /**
   * The items to display
   */
  items: DropdownItem[]
}

export function DropdownMenu(props: DropdownMenuProps) {
  const styles = dropdownMenuStyles()
  return (
    <D.Root>
      <D.Trigger asChild>{props.trigger}</D.Trigger>
      <D.Portal>
        <D.Content align={props.align} side={props.side} sideOffset={0} className={cn(styles.content(), "p-2 md:p-1")}>
          <D.Arrow asChild>
            <div className={popoverStyles().arrow()}>
              <svg width={30} height={10} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 45 30">
                <path d="M0 0l22.5 22.5L45 0z" />
              </svg>
            </div>
          </D.Arrow>
          <DropdownItems items={props.items} />
        </D.Content>
      </D.Portal>
    </D.Root>
  )
}

const stringLabel = (...labels: (ReactNode | string | null | undefined)[]) => {
  return labels.find((label): label is string => typeof label === "string")
}

const ITEM_TYPES = {
  item: ({ item }) => {
    const styles = dropdownItemStyles({})
    return (
      <D.Item
        className={styles.base({ className: item.className })}
        textValue={stringLabel(item.textValue, item.label)}
        onSelect={item.onSelect}
        disabled={item.disabled}
      >
        <span className={styles.indicator()}>
          <Icon name={item.icon} />
        </span>
        <span className={styles.label()}>{item.label}</span>
      </D.Item>
    )
  },
  heading: ({ item }) => {
    const styles = dropdownItemStyles({ selectable: false, caption: true })
    return (
      <D.Label className={styles.base()}>
        <span className={styles.indicator()}></span>
        <span className={styles.label()}>{item.label}</span>
      </D.Label>
    )
  },
  checkbox: ({ item }) => {
    const styles = dropdownItemStyles({})
    return (
      <D.CheckboxItem
        checked={item.checked}
        disabled={item.disabled}
        textValue={stringLabel(item.textValue, item.label)}
        className={styles.base({ className: item.className })}
        onSelect={(e) => {
          const checked = !item.checked
          item.onCheckedChange?.(checked)
          e.preventDefault()
        }}
      >
        <span className={styles.indicator()}>
          <Checkbox checked={item.checked} />
        </span>
        <span className={styles.label()}>{item.label}</span>
      </D.CheckboxItem>
    )
  },
  radio: ({ item }) => {
    const headingStyles = dropdownItemStyles({ caption: true })
    const styles = dropdownItemStyles({})
    return (
      <Fragment>
        {item.label && (
          <D.Label className={headingStyles.base()}>
            <span className={headingStyles.indicator()}></span>
            <span className={headingStyles.label()}>{item.label}</span>
          </D.Label>
        )}
        <D.RadioGroup
          className={cn(dropdownMenuStyles().items())}
          value={item.value}
          onValueChange={item.onValueChange}
        >
          {item.options.map((option, index) => (
            <D.RadioItem
              value={option.value}
              disabled={option.disabled || item.disabled}
              textValue={stringLabel(item.label, item.value)}
              className={styles.base()}
            >
              <span className={styles.indicator()}>
                <D.ItemIndicator>
                  <DotFilledIcon />
                </D.ItemIndicator>
              </span>
              <span className={styles.label()}>{item.label}</span>
            </D.RadioItem>
          ))}
        </D.RadioGroup>
      </Fragment>
    )
  },
  separator: ({ item }) => {
    return <D.Separator className={dropdownMenuStyles().separator()} />
  },
  subnav: ({ item }) => {
    const styles = dropdownItemStyles({})
    return (
      <D.Sub>
        <D.SubTrigger className={styles.base()}>
          <span className={styles.indicator()}></span>
          <span className={styles.label()}>{item.label}</span>
          <span className={styles.arrow()}>
            <CaretRightIcon />
          </span>
        </D.SubTrigger>
        <D.Portal>
          <D.SubContent className={dropdownMenuStyles().content()} sideOffset={2} alignOffset={-5}>
            <DropdownItems items={item.items} />
          </D.SubContent>
        </D.Portal>
      </D.Sub>
    )
  },
} satisfies {
  [K in DropdownItem["type"]]: (props: { item: Extract<DropdownItem, { type: K }> }) => ReactNode
}

function DropdownItems(props: { items: DropdownItem[] }) {
  return (
    <div className="flex flex-col gap-1">
      {props.items.map((item, index) => {
        const Component = ITEM_TYPES[item.type] as any
        if (!Component) return null
        return <Component key={index} item={item} />
      })}
    </div>
  )
}
